'use client';

import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { CSSProperties, useEffect, useState } from 'react';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '../ui/carousel';

const Index = ({
  colorLabelMap,
  colorIdMap,
}: {
  colorIdMap: Record<string, string>;
  colorLabelMap: Record<
    string,
    NonNullable<
      NonNullable<
        NonNullable<NonNullable<GetFallBridesmaidDressesFilterQuery['products']>['aggregations']>[0]
      >['options']
    >[0]
  >;
}) => {
  const headerContent = (
    <>
      <h1 className='text-center font-Shango text-[26px] leading-[31px] text-white lg_text-[46px] lg_leading-[56px]'>
        FALL BRIDESMAID DRESS {websiteEnv.isUnitedKingdom ? 'COLOUR' : 'COLOR'} PALETTE
      </h1>
      <p className='mt-0.5 text-center text-sm leading-none text-white lg_mt-2 lg_text-[20px] lg_leading-[28px]'>
        Rich fall {websiteEnv.isUnitedKingdom ? 'colours' : 'colors'} and elegant designs for your bridal party.
      </p>
    </>
  );

  return (
    <div className='pb-5 lg_pb-[193px]'>
      <div
        className='relative bg-cover bg-center bg-no-repeat py-[19px] lg_hidden lg_py-[54px]'
        style={{
          backgroundImage: `url("${process.env.NEXT_PUBLIC_BASE_PATH}/images/fall-bridesmaid-dresses/banner-m.png")`,
        }}
      >
        {headerContent}
      </div>
      <div
        className='relative hidden bg-cover bg-center bg-no-repeat py-[19px] lg_block lg_py-[54px]'
        style={{
          backgroundImage: `url("${process.env.NEXT_PUBLIC_BASE_PATH}/images/fall-bridesmaid-dresses/banner.png")`,
        }}
      >
        {headerContent}
      </div>

      <ul className='mx-auto mt-0.5 flex max-w-site flex-col gap-y-0 lg_mt-[45px] lg_gap-y-[45px]'>
        {configs.map(
          (
            {
              cover,
              mobileCover,
              label,
              fabricSrc,
              mobileFabricSrc,
              shopColorsFamilyText,
              colorsFamilyHref,
              colorOptions,
              shopColorsFamilyClassName,
            },
            i,
          ) => {
            const options = colorOptions.reduce<
              {
                label: string;
                default_label: string;
                rewrite: string;
                colorRectStyle: CSSProperties;
                colorImageUrl: null | string;
              }[]
            >((acc, cur) => {
              const option = colorLabelMap[cur.defaultLabel];
              const optionId = option?.value;
              const rewrite = optionId ? colorIdMap[optionId] : null;

              if (!optionId || !rewrite || !option.label) return acc;

              const isImage = cur.colorType === 'image';

              acc.push({
                label: option.label,
                default_label: cur.defaultLabel,
                rewrite,
                colorImageUrl: isImage && cur.color ? cur.color : null,
                colorRectStyle:
                  isImage && cur.color
                    ? {}
                    : createColorRectStyle(option.swatch_data?.thumbnail || '', option.swatch_data?.value || ''),
              });

              return acc;
            }, []);

            return (
              <li
                className={clsx('flex flex-col ', i % 2 === 0 ? 'lg_flex-row' : 'lg_flex-row-reverse')}
                key={i}
              >
                <div className='relative flex-1 lg_w-1/2'>
                  <Image
                    alt={label}
                    src={cover}
                    width={720}
                    height={520}
                    quality={100}
                    className='hidden h-auto w-full lg_block'
                  />
                  <Image
                    alt={label}
                    src={mobileCover}
                    width={750}
                    height={400}
                    quality={100}
                    className='block h-auto w-full lg_hidden'
                  />
                  <div className='absolute bottom-2.5 left-1/2 -translate-x-1/2 text-center font-Shango text-[22px] text-white lg_bottom-[50px] lg_text-[30px]'>
                    {label}
                  </div>
                </div>

                <div className='flex-1 bg-[#F3EFEC] lg_flex lg_w-1/2 lg_flex-col lg_justify-center'>
                  <div className='px-5 pt-[15px] lg_pt-0'>
                    <div className='relative mx-auto lg_static lg_max-w-[522px]'>
                      <Image
                        alt={label}
                        src={fabricSrc}
                        width={522}
                        height={167}
                        quality={100}
                        className='hidden h-auto w-full lg_block'
                      />
                      <Image
                        alt={label}
                        src={mobileFabricSrc}
                        width={335}
                        height={121}
                        quality={100}
                        className='h-auto w-full lg_hidden'
                      />
                      <Link
                        href={colorsFamilyHref}
                        className={clsx(
                          'absolute bottom-2.5 left-1/2 block min-w-[160px] -translate-x-1/2 py-2.5 text-center text-[15px]  font-medium leading-[21px] text-white underline underline-offset-[2px] lg_static  lg_bottom-0 lg_left-0 lg_block lg_min-w-auto lg_-translate-x-0 lg_py-[13px] lg_text-[17px] lg_leading-6',
                          shopColorsFamilyClassName,
                        )}
                      >
                        {shopColorsFamilyText}
                      </Link>
                    </div>
                  </div>
                  <ColorCarousel options={options} />
                </div>
              </li>
            );
          },
        )}
      </ul>
    </div>
  );
};

const ColorCarousel = ({
  options,
}: {
  options: {
    label: string;
    default_label: string;
    rewrite: string;
    colorRectStyle: CSSProperties;
    colorImageUrl: string | null;
  }[];
}) => {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [slidesLessThanViewport, setSlidesLessThanViewport] = useState(false);

  useEffect(() => {
    if (!api) {
      return;
    }

    const onSelect = (api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api]);

  useEffect(() => {
    const handler = (api: CarouselApi) => {
      const { containerRect, slideRects } = api?.internalEngine() || {};
      const lastSlideRect = slideRects ? [...slideRects].pop() : null;
      const slidesLessThanViewport = !!containerRect && !!lastSlideRect && containerRect.right > lastSlideRect.right;
      setSlidesLessThanViewport(slidesLessThanViewport);
    };

    if (api) {
      handler(api);
    }

    api?.on('reInit', handler);

    return () => {
      api?.off('reInit', handler);
    };
  }, [api]);

  return (
    <div className='flex justify-center gap-x-[15px] px-0.5 lg_mt-[30px] lg_gap-x-2.5'>
      <button
        type='button'
        className={clsx(
          'flex w-10 flex-none items-center justify-center',
          !canScrollPrev && !canScrollNext ? 'invisible' : '',
        )}
        onClick={() => {
          api?.scrollPrev();
        }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='23'
          fill='none'
          viewBox='0 0 12 23'
        >
          <path
            stroke='#80583F'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='m10.614 1.5-9.231 10 9.23 10'
          ></path>
        </svg>
      </button>
      <div className='max-w-[530px] flex-1 overflow-hidden py-[15px] lg_py-0'>
        <Carousel
          opts={{ align: 'start', dragFree: true }}
          setApi={setApi}
        >
          <CarouselContent
            className={clsx('-ml-2.5 flex', slidesLessThanViewport ? 'justify-center' : 'justify-start')}
          >
            {options.map(({ rewrite, label, colorRectStyle, colorImageUrl }, i) => {
              return (
                <CarouselItem
                  key={i}
                  className=' basis-[90px] pl-2.5 pt-[5px]'
                >
                  <Link
                    href={`/bridesmaid-dresses/color/${rewrite}`}
                    className='flex flex-col items-center'
                  >
                    <div
                      className='h-[50px] w-[50px] overflow-hidden rounded-[5px] bg-center bg-no-repeat  lg_h-[66px] lg_w-[66px]'
                      style={colorRectStyle}
                    >
                      {colorImageUrl ? (
                        <Image
                          alt={label}
                          width={132}
                          height={132}
                          src={colorImageUrl}
                          className='h-auto w-full'
                          quality={100}
                        />
                      ) : null}
                    </div>
                    <div className='mt-2.5 text-center text-[15px] leading-[1.2]'>{label}</div>
                  </Link>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
      <button
        type='button'
        className={clsx(
          'flex w-10 flex-none items-center justify-center',
          !canScrollPrev && !canScrollNext ? 'invisible' : '',
        )}
        onClick={() => {
          api?.scrollNext();
        }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='23'
          fill='none'
          viewBox='0 0 12 23'
        >
          <g clipPath='url(#clip0_15843_32071)'>
            <path
              stroke='#80583F'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='m1.386 21.5 9.231-10-9.23-10'
            ></path>
          </g>
          <defs>
            <clipPath id='clip0_15843_32071'>
              <path
                fill='#fff'
                d='M11.617 22.5H.387V.5h11.23z'
              ></path>
            </clipPath>
          </defs>
        </svg>
      </button>
    </div>
  );
};

const imageBathPath = process.env.NEXT_PUBLIC_BASE_PATH;

const configs: {
  label: string;
  cover: string;
  mobileCover: string;
  fabricSrc: string;
  mobileFabricSrc: string;
  colorsFamilyHref: string;
  shopColorsFamilyText: string;
  shopColorsFamilyClassName: string;
  colorOptions: {
    defaultLabel: string;
    color?: string;
    colorType: 'color' | 'image';
  }[];
}[] = [
  {
    label: 'AMBER FOREST',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/floral-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/floral-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/floral-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/floral-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/floral`,
    shopColorsFamilyText: 'SHOP FLORAL',
    shopColorsFamilyClassName: 'bg-[#0D595A]',
    colorOptions: [
      {
        defaultLabel: 'Amber Forest',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/amber-forest.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Pink Cherry Blossom',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/pink-cherry-blossom.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Peach Blossom Breeze',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/peach-blossom-breeze.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Oceanic Fantasy Rose',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/oceanic-fantasy-rose.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Blue Botanical Floral',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/blue-botanical-floral.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Dusty Blue Peonies',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/dusty-blue-peonies.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Sage Garden Party',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/sage-garden-party.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Trumpet Teal Rose',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/trumpet-teal-rose.png`,
        colorType: 'image',
      },
      {
        defaultLabel: 'Tea Green Shade',
        color: `${imageBathPath}/images/fall-bridesmaid-dresses/tea-green-shade.png`,
        colorType: 'image',
      },
    ],
  },
  {
    label: 'HUNTER GREEN',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/green-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/green-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/green-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/green-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/green`,
    shopColorsFamilyText: 'SHOP GREEN',
    shopColorsFamilyClassName: 'bg-[#1B443E]',
    colorOptions: [
      {
        defaultLabel: 'Hunter Green',
        colorType: 'color',
      },
      {
        defaultLabel: 'Dark Emerald',
        colorType: 'color',
      },
      {
        defaultLabel: 'Evergreen',
        colorType: 'color',
      },
      {
        defaultLabel: 'Jolly Green',
        colorType: 'color',
      },
      {
        defaultLabel: 'Deep Lake',
        colorType: 'color',
      },
      {
        defaultLabel: 'Oil Green',
        colorType: 'color',
      },
      {
        defaultLabel: 'Dark Olive',
        colorType: 'color',
      },
      {
        defaultLabel: 'Fennel Seed',
        colorType: 'color',
      },
      {
        defaultLabel: 'Green Olive',
        colorType: 'color',
      },
    ],
  },
  {
    label: 'ROYAL BLUE',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/blue-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/blue-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/blue-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/blue-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/blue`,
    shopColorsFamilyText: 'SHOP BLUE',
    shopColorsFamilyClassName: 'bg-[#1A536E]',
    colorOptions: [
      {
        defaultLabel: 'Ink blue',
        colorType: 'color',
      },
      {
        defaultLabel: 'Navy',
        colorType: 'color',
      },
      {
        defaultLabel: 'Royal Blue',
        colorType: 'color',
      },
      {
        defaultLabel: 'Midnight Navy',
        colorType: 'color',
      },
      {
        defaultLabel: 'Dark Navy',
        colorType: 'color',
      },
    ],
  },
  {
    label: 'DEEP CLARET',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/red-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/red-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/red-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/red-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/red`,
    shopColorsFamilyText: 'SHOP RED',
    shopColorsFamilyClassName: 'bg-[#BE574D]',
    colorOptions: [
      {
        defaultLabel: 'Rust',
        colorType: 'color',
      },
      {
        defaultLabel: 'Jester Red',
        colorType: 'color',
      },
      {
        defaultLabel: 'Deep Claret',
        colorType: 'color',
      },
      {
        defaultLabel: 'Tango Red',
        colorType: 'color',
      },
      {
        defaultLabel: 'Burgundy',
        colorType: 'color',
      },
      {
        defaultLabel: 'Chianti',
        colorType: 'color',
      },
    ],
  },
  {
    label: 'BURNISHED GOLD',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/yellow-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/yellow-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/yellow-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/yellow-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/yellow`,
    shopColorsFamilyText: 'SHOP YELLOW',
    shopColorsFamilyClassName: 'bg-[#E0984F]',
    colorOptions: [
      {
        defaultLabel: 'Mustard Yellow',
        colorType: 'color',
      },
      {
        defaultLabel: 'Mango',
        colorType: 'color',
      },
      {
        defaultLabel: 'Burnished Gold',
        colorType: 'color',
      },
    ],
  },
  {
    label: 'TANGERINE',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/orange-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/orange-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/orange-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/orange-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/orange`,
    shopColorsFamilyText: 'SHOP ORANGE',
    shopColorsFamilyClassName: 'bg-[#CD5F3E]',
    colorOptions: [
      {
        defaultLabel: 'Raw Sienna',
        colorType: 'color',
      },
      {
        defaultLabel: 'Tangerine',
        colorType: 'color',
      },
      {
        defaultLabel: 'Burnt Orange',
        colorType: 'color',
      },
    ],
  },
  {
    label: 'BLACK',
    cover: `${imageBathPath}/images/fall-bridesmaid-dresses/black-cover.png`,
    mobileCover: `${imageBathPath}/images/fall-bridesmaid-dresses/black-cover-m.png`,
    fabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/black-fabric.png`,
    mobileFabricSrc: `${imageBathPath}/images/fall-bridesmaid-dresses/black-fabric-m.png`,
    colorsFamilyHref: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/black`,
    shopColorsFamilyText: 'SHOP DARK',
    shopColorsFamilyClassName: 'bg-[#2A2A2A]',
    colorOptions: [
      {
        defaultLabel: 'Steel Gray',
        colorType: 'color',
      },
      {
        defaultLabel: 'Black Metallic',
        colorType: 'color',
      },
      {
        defaultLabel: 'Black',
        colorType: 'color',
      },
    ],
  },
];

const createColorRectStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${process.env.NEXT_PUBLIC_BASE_PATH}/media/attribute/swatch/swatch_thumb/110x90${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

export default Index;
